import { useEffect, useRef } from 'react';
import { useWebSocket } from '../../context/WebSocketContext';

// 訂閱既有 WebSocketProvider 廣播中的 chat 事件（沿用全域唯一一條 WS 連線，不另開連線）。
// Payload 對齊後端 app/routers/chat.py 的兩處廣播、app/routers/websockets.py broadcast_chat_message()：
//   { type:'chat_message',          conversation_id, student_id, student_name, class_id, message:{...} }
//   { type:'chat_message_recalled', conversation_id, student_id, class_id, message_id,
//                                   message:{...同一則訊息，is_recalled=true、text/image_url 已清空} }
//
// 用法：
//   useChatSocket((payload) => { ...依 payload.conversation_id 決定要不要處理... })       // 只收新訊息（原本的行為）
//   useChatSocket(onChatMessage, onMessageRecalled)                                       // 兩種事件分開收
//
// 【為什麼回收事件另開第二個參數，而不是併進同一個 callback】
// 現有呼叫端（六個檔案、七處）收到 payload 之後都不看 payload.type，直接就
// `appendMessage(payload.message)` 或「整份重抓」。若把回收事件混進同一個 callback：
//   ① 對話室會把「被收回的那則」當成新訊息 append —— 已載入的那則會被既有的 id 去重擋掉
//      （＝畫面毫無反應，等於收不到效果，因為去重是「保留舊的」而不是「換成新的」），
//      沒載入到的那則反而會憑空冒到對話最下面。
//   ② 對話室還會順手多打一次 POST read（把「有人收回訊息」誤當成「有人來訊」）。
// 也就是說多收一種事件對既有呼叫端並非無害，必須讓每個呼叫端明確選擇要不要收。
// 第二個參數不傳 ＝ 行為與加這段之前逐字相同。
//
// 【為什麼不是改成 options 物件 `useChatSocket({ onMessage, onRecalled })`】
// 那是破壞性改動（七處呼叫全要改寫，改壞了是即時更新整個失效這種不易察覺的壞法），
// 而目前只多這一種事件，位置參數就夠用。真的再長出第三、四種事件時再收斂成物件也不遲。
export default function useChatSocket(onChatMessage, onMessageRecalled) {
    const ws = useWebSocket();
    const lastMessage = ws?.lastMessage;
    const handlerRef = useRef(onChatMessage);
    handlerRef.current = onChatMessage;
    // 同樣用 ref 存最新的 handler：呼叫端幾乎都是行內箭頭函式（每次 render 都是新參考），
    // 直接放進 effect 相依會變成每 render 重跑一次 effect。
    const recallHandlerRef = useRef(onMessageRecalled);
    recallHandlerRef.current = onMessageRecalled;

    useEffect(() => {
        if (!lastMessage) return;
        if (lastMessage.type === 'chat_message') {
            handlerRef.current?.(lastMessage);
        } else if (lastMessage.type === 'chat_message_recalled') {
            recallHandlerRef.current?.(lastMessage);
        }
        // 只依 lastMessage：兩個 handler 都放在 ref 裡（呼叫端多半傳行內箭頭函式），
        // 不會、也不該讓「呼叫端重新 render」變成一次事件重放。
        //
        // ⚠ 下面這行 eslint-disable 看起來是多餘的（eslint 會回報 "Unused eslint-disable
        // directive"），**但不要刪**：本專案的 eslint-plugin-react-hooks 是新版 compiler 規則，
        // 一個函式裡只要出現任何 react-hooks 的 disable 註解，整個函式就會被排除在 compiler
        // 分析之外。刪掉之後，上面兩行「render 期間寫 ref.current」（這個檔案從第一版就是這樣
        // 寫的 latest-ref 慣用法）會被 react-hooks/refs 判成 error。刪它＝憑空多兩個 error，
        // 而那兩行的行為本來就是對的。這是 2026-08-16 實測出來的，別再刪一次。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastMessage]);
}
